import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  ProcedureMethodExecutionInstanceStatus,
  ProcessTaskStatusType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRun,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {
  PredecessorEdge,
  resolveTasklessStatuses,
  TERMINAL_PROCESS_TASK_STATUS_TYPES,
} from 'studies/typed/shared/procedure-runs/execution-status/activeMethodUtils';
import * as DateUtil from 'util/DateUtil';
import * as logging from 'util/Logging';
import {assertNever} from 'util/Types';

import {
  ProcedureRunDetailsViewQuery_procedureRun_ProcedureRunNode as ProcedureRunNode,
  ProcedureRunDetailsViewQuery_procedureRun_ProcedureRunNode_conditions_ProcedureRunConditionNode as ProcedureRunConditionNode,
  ProcedureRunDetailsViewQuery_procedureRun_ProcedureRunNode_conditions_ProcedureRunConditionNode_procedureRunConditionReplicates_ProcedureRunConditionReplicateNode as ProcedureRunConditionReplicateNode,
  ProcedureRunDetailsViewQuery_procedureRun_ProcedureRunNode_methodExecutionInstances_ProcedureMethodExecutionInstanceNode as ProcedureMethodExecutionInstanceNode,
  ProcedureRunDetailsViewQuery_procedureRun_ProcedureRunNode_templateProcedureFlowchartVersion_InaccessibleInfoNode as InaccessibleFlowchartVersion,
  ProcedureRunDetailsViewQuery_procedureRun_ProcedureRunNode_templateProcedureFlowchartVersion_ProcedureFlowchartVersionNode as ProcedureFlowchartVersionNode,
  ProcedureRunDetailsViewQuery_procedureRun_ProcedureRunNode_templateProcedureFlowchartVersion_ProcedureFlowchartVersionNode_nodes_ProcedureMethodDefinitionVersionNode as ProcedureMethodDefinitionVersionNode,
} from '../design-tabs/__generated/ProcedureRunDetailsViewQuery';

/**
 * The subset of a procedure run consumed by {@link buildMultiProcedureRunStudyExecutionProgress}.
 *
 * Reuses node shapes from `ProcedureRunDetailsViewQuery` but omits fields the builder never reads.
 */
export type ProcedureRunOverviewInput = Omit<
  ProcedureRunNode,
  'description' | 'procedureType' | 'templateProcedureFlowchartVersion'
> & {
  templateProcedureFlowchartVersion:
    | InaccessibleFlowchartVersion
    | Omit<ProcedureFlowchartVersionNode, 'migratedToFlowchartConfigVersion'>;
};

interface MaybeArchivedPmei {
  __typename: 'ProcedureMethodExecutionInstanceNode';
  archiveData: {archived: boolean};
}

/**
 * Drops archived PMEIs so they are hidden from procedure run views. Generic over the PMEI shape so it can
 * be reused across queries that select `archiveData.archived`.
 */
export function excludeArchivedPmeis<T extends MaybeArchivedPmei>(pmeis: ReadonlyArray<T>): Array<T> {
  return pmeis.filter((pmei) => !pmei.archiveData.archived);
}

export interface ProcedureRunOverview {
  procedureRunId: string;
  displayId: string | null;
  name: string;
  procedureTemplateId: string;
  createdAt: string;
  unitOperationMethods: Array<UnitOperationMethod>;
  conditions: Array<Condition>;
  unitOperationExecutionInstances: Array<UnitOperationExecutionInstance>;
}

export interface UnitOperationMethod {
  id: string;
  name: string;
}

export interface Condition {
  id: string;
  name: string;
  description: string;
  /**
   * The owning method version id, or the inaccessible info's `invalidId` if the method is
   * inaccessible.
   */
  templateMethodVersionId: string;
  replicates: Array<Replicate>;
}

/**
 * Pairing predicate for the overview's `methods × conditions × replicates` join. Each per-method
 * condition applies only to its owning method.
 */
export function conditionAppliesToMethod(condition: Condition, methodId: string): boolean {
  return condition.templateMethodVersionId === methodId;
}

export interface Replicate {
  id: string;
  replicateNumber: number;
  processRootTaskId: string | null;
}

export interface UnitOperationExecutionInstance {
  id: string;
  methodId: string | null;
  conditionId: string;
  replicateNumber: number;
  worksheetId: string | null;
  worksheetCreatedAt: string | null;
  worksheetUrl: string | null;
  status: TaskStatus | null;
  /**
   * The PMEI's own execution status, independent of its method task status. Populated when the
   * backend provides it and surfaced in split-pool views where the PMEI status is authoritative.
   */
  PMEIStatus: ProcedureMethodExecutionInstanceStatus | null;
  previousPMEITasksCompleted: boolean;
  linkedUnitOperationExecutionInstanceIds: Array<string>;
}

export interface TaskStatus {
  id: string;
  statusType: ProcessTaskStatusType;
}

export type PLANNED = 'PLANNED';
export const PLANNED: PLANNED = 'PLANNED';
export type UnitOperationStatusType = ProcessTaskStatusType | PLANNED;

/**
 * Maps a PMEI's own status (`ProcedureMethodExecutionInstanceStatus`) to the
 * {@link UnitOperationStatusType} consumed by the status visuals. A null or unrecognized PMEI status maps
 * to PENDING.
 */
export function unitOperationStatusFromPmeiStatus(
  pmeiStatus: ProcedureMethodExecutionInstanceStatus | null
): UnitOperationStatusType {
  switch (pmeiStatus) {
    case null:
    case ProcedureMethodExecutionInstanceStatus.PENDING:
      return ProcessTaskStatusType.PENDING;
    case ProcedureMethodExecutionInstanceStatus.PLANNED:
      return PLANNED;
    case ProcedureMethodExecutionInstanceStatus.IN_PROGRESS:
      return ProcessTaskStatusType.IN_PROGRESS;
    case ProcedureMethodExecutionInstanceStatus.COMPLETED:
      return ProcessTaskStatusType.COMPLETED;
    case ProcedureMethodExecutionInstanceStatus.SKIPPED:
      return ProcessTaskStatusType.SKIPPED;
    case ProcedureMethodExecutionInstanceStatus.FAILED:
      return ProcessTaskStatusType.FAILED;
    default:
      logging.error(`Unexpected PMEI status: ${assertNever(pmeiStatus)}`);
      return ProcessTaskStatusType.PENDING;
  }
}

/**
 * Resolves the single status used to render, filter, and count a unit operation, keeping those
 * three in sync.
 *
 * With split-pool enabled the PMEI status is authoritative and a null PMEI status falls back to
 * PENDING. With split-pool disabled the method task status is used and a null task status falls
 * back to PLANNED.
 */
export function resolveUnitOperationStatus(
  instance: UnitOperationExecutionInstance
): UnitOperationStatusType {
  if (BenchlingConfig.STUDIES_ENABLE_SPLIT_POOL) {
    return unitOperationStatusFromPmeiStatus(instance.PMEIStatus);
  }
  return instance.status?.statusType ?? PLANNED;
}

/**
 * Calculates the count of unit operations by their resolved status.
 *
 * @param overview - The procedure run overview.
 * @returns A map containing the count of unit operations for each status. If the status is not included in the map, we can assume that there are no unit operations with that status.
 */
export function getUnitOperationCountByTaskStatus(
  overview: ProcedureRunOverview
): Map<UnitOperationStatusType, number> {
  return overview.unitOperationExecutionInstances.reduce((counts, instance) => {
    const statusType = resolveUnitOperationStatus(instance);
    counts.set(statusType, (counts.get(statusType) ?? 0) + 1);
    return counts;
  }, new Map<UnitOperationStatusType, number>());
}

/**
 * Checks if a procedure run has accessible template and parent data.
 *
 * @param procedureRun - The procedure run to check.
 * @returns True if the procedure run has accessible data, false otherwise.
 */
export function doesProcedureRunHaveAccessibleData(procedureRun: ProcedureRunOverviewInput): boolean {
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(procedureRun);
  return !isInaccessible(flowchartVersion);
}

/**
 * Builds the process development study execution progress overview based on the procedure run data.
 *
 * @param procedureRun - The procedure run data.
 * @returns The process development study execution progress overview.
 */
export function buildMultiProcedureRunStudyExecutionProgress(
  procedureRun: ProcedureRunOverviewInput
): ProcedureRunOverview {
  // Build unit operation methods
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(procedureRun);
  const unitOperationMethods: Array<UnitOperationMethod> =
    !isInaccessible(flowchartVersion) && flowchartVersion.nodes != null
      ? flowchartVersion.nodes.map((method: ProcedureMethodDefinitionVersionNode) => ({
          id: method.id,
          name: method.name,
        }))
      : [];

  // Build conditions. `templateMethodVersionId` tags each backend row with its owning method
  // so downstream rendering can scope the methods × conditions join per method.
  const conditions: Array<Condition> = procedureRun.conditions.map(
    (condition: ProcedureRunConditionNode) => ({
      id: condition.id,
      name: condition.name,
      description: condition.description,
      templateMethodVersionId: getAccessibleOrInaccessibleId(condition.templateMethodVersion),
      replicates: condition.procedureRunConditionReplicates.map(
        (replicate: ProcedureRunConditionReplicateNode) => ({
          id: replicate.id,
          replicateNumber: replicate.replicateNumber,
          processRootTaskId:
            replicate.processRootTask != null
              ? getAccessibleOrInaccessibleId(replicate.processRootTask)
              : null,
        })
      ),
    })
  );

  // Hide archived PMEIs from the overview (and everything derived from it, e.g. the params table).
  const methodExecutionInstances = excludeArchivedPmeis(procedureRun.methodExecutionInstances);

  // Build unit operation execution instances
  const unitOperationExecutionInstances: Array<UnitOperationExecutionInstance> = methodExecutionInstances.map(
    (methodExecutionInstance: ProcedureMethodExecutionInstanceNode) => {
      const previousPMEIs = methodExecutionInstance.previousProcedureMethodExecutionInstances ?? [];
      const previousPMEITasksCompleted =
        previousPMEIs.length === 0 ||
        previousPMEIs.every((prevPmei) => {
          if (isInaccessible(prevPmei)) {
            return false;
          }
          const task = prevPmei.methodTask;
          if (task == null || isInaccessible(task)) {
            return false;
          }
          return TERMINAL_PROCESS_TASK_STATUS_TYPES.has(task.status.statusType);
        });

      return {
        id: methodExecutionInstance.id,
        methodId: isInaccessible(methodExecutionInstance.templateMethodVersion)
          ? null
          : methodExecutionInstance.templateMethodVersion.id,
        conditionId: methodExecutionInstance.procedureRunConditionReplicate.procedureRunCondition.id,
        replicateNumber: methodExecutionInstance.procedureRunConditionReplicate.replicateNumber,
        worksheetId:
          methodExecutionInstance.worksheet == null || isInaccessible(methodExecutionInstance.worksheet)
            ? null
            : methodExecutionInstance.worksheet.id,
        worksheetCreatedAt:
          methodExecutionInstance.worksheet == null || isInaccessible(methodExecutionInstance.worksheet)
            ? null
            : DateUtil.readableTime(methodExecutionInstance.worksheet.createdAt, {showTime: true}),
        worksheetUrl:
          methodExecutionInstance.worksheet == null || isInaccessible(methodExecutionInstance.worksheet)
            ? null
            : methodExecutionInstance.worksheet.editUrl,
        status:
          methodExecutionInstance.methodTask == null || isInaccessible(methodExecutionInstance.methodTask)
            ? null
            : {
                id: methodExecutionInstance.methodTask.id,
                statusType: methodExecutionInstance.methodTask.status.statusType,
              },
        PMEIStatus: methodExecutionInstance.status,
        previousPMEITasksCompleted,
        linkedUnitOperationExecutionInstanceIds:
          methodExecutionInstance.sourceProcedureMethodExecutionInstanceAssociations == null
            ? []
            : methodExecutionInstance.sourceProcedureMethodExecutionInstanceAssociations.map(
                (sourceProcedureMethodExecutionInstanceAssociation) =>
                  isInaccessible(sourceProcedureMethodExecutionInstanceAssociation)
                    ? sourceProcedureMethodExecutionInstanceAssociation.invalidId
                    : sourceProcedureMethodExecutionInstanceAssociation.sourceProcedureMethodExecutionInstance
                        .id
              ),
      };
    }
  );

  // Pre-populate taskless PMEIs as PENDING when they are ready to start.
  // Split-pool: per-PMEI check — PENDING when all predecessors are terminal.
  // Legacy: only first-method taskless PMEIs get PENDING (tasks are created async).
  const predecessorEdges: Array<PredecessorEdge> = [];
  const statusMap = new Map<string, ProcessTaskStatusType | null>();
  const pmeiMethodIds = new Map<string, string>();
  for (const instance of unitOperationExecutionInstances) {
    statusMap.set(instance.id, instance.status?.statusType ?? null);
    if (instance.methodId != null) {
      pmeiMethodIds.set(instance.id, instance.methodId);
    }
  }
  for (const pmei of methodExecutionInstances) {
    for (const prev of pmei.previousProcedureMethodExecutionInstances ?? []) {
      const prevId = getAccessibleOrInaccessibleId(prev);
      predecessorEdges.push({sourcePmeiId: prevId, targetPmeiId: pmei.id});
      if (!statusMap.has(prevId)) {
        if (isInaccessible(prev)) {
          statusMap.set(prevId, null);
        } else {
          const task = prev.methodTask;
          const predStatus = task == null || isInaccessible(task) ? null : task.status.statusType;
          statusMap.set(prevId, predStatus);
        }
      }
    }
  }
  const resolvedStatuses = resolveTasklessStatuses(
    statusMap,
    predecessorEdges,
    unitOperationMethods,
    pmeiMethodIds
  );
  unitOperationExecutionInstances.forEach((instance) => {
    if (instance.status == null) {
      const resolved = resolvedStatuses.get(instance.id);
      if (resolved != null) {
        instance.status = {id: '', statusType: resolved};
      }
    }
  });

  const parent = !isInaccessible(flowchartVersion)
    ? getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion)
    : null;
  // Procedure flowchart parent is null when the type is procedure run, but we expect it to be of type
  // procedure here as the procedure holds the template
  assert(parent != null, 'Expected procedure flowchart parent to be non-null.');

  // Build the overview object
  const overview: ProcedureRunOverview = {
    procedureRunId: procedureRun.id,
    name: procedureRun.name,
    displayId: procedureRun.displayId,
    procedureTemplateId: parent.id,
    createdAt: procedureRun.createdAt,
    unitOperationMethods,
    conditions,
    unitOperationExecutionInstances,
  };

  return overview;
}
